import { useState, FormEvent, useEffect } from 'react';
import { navigate } from '@/hooks/useRouter';
import { Eye, EyeOff, User, Lock, Ticket, CircleUser } from 'lucide-react';
import NavBar from '@/components/ui/NavBar';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import AlertDialog from '@/components/ui/AlertDialog';
import { register } from '@/services/authService';
import { useSiteConfig, useInviteCode, useToast } from '@/contexts/GlobalProvider';

export default function Page() {
  const { config } = useSiteConfig();
  const { inviteCode: storedInviteCode, isFromUrl } = useInviteCode();
  const toast = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  // 优先从 URL 参数读取邀请码（防刷新丢失），其次从 Context（localStorage）读取
  const urlInviteCode = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('inviteCode')
    : null;
  const initialInviteCode = urlInviteCode || storedInviteCode || '';
  const [inviteCode, setInviteCode] = useState(initialInviteCode);
  // 有预设邀请码时锁定不可修改
  const inviteCodeLocked = !!(urlInviteCode || isFromUrl || storedInviteCode);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  // 注册结果对话框状态
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'success' | 'info'>('success');
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogDescription, setDialogDescription] = useState('');

  // 监听 storedInviteCode 变化（URL 变化时自动更新）
  useEffect(() => {
    if (storedInviteCode && !inviteCode) {
      setInviteCode(storedInviteCode);
    }
  }, [storedInviteCode]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!name || !username || !password || !confirmPassword) {
      toast.warning('请填写所有必填项');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('两次输入的密码不一致');
      return;
    }

    setLoading(true);
    try {
      const response = await register({
        name: name,
        account: username,
        password: password,
        inviteCode: inviteCode || undefined,
      });

      // 根据审核状态显示不同对话框
      if (response.pendingReview) {
        setDialogType('info');
        setDialogTitle('提交成功');
        setDialogDescription('您的注册申请已提交，请耐心等待管理员审核。审核通过后即可登录。');
      } else {
        setDialogType('success');
        setDialogTitle('注册成功');
        setDialogDescription('恭喜您注册成功！点击确定前往登录。');
      }
      setDialogOpen(true);
    } catch (error: any) {
      if (error.code) {
        toast.error(error.message || '注册失败，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleIcon = (visible: boolean, toggle: () => void) => (
    <button
      type="button"
      onClick={toggle}
      className="text-white/60 hover:text-white transition-colors"
      aria-label={visible ? '隐藏密码' : '显示密码'}
    >
      {visible ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  );

  return (
    <div className="min-h-screen flex flex-col bg-bg-page">
      <NavBar
        title={`${config?.clientName || '用户端'}注册`}
        className="bg-white border-b border-border-color"
      />

      <div className="flex-1 flex flex-col items-center px-4 pt-20 pb-8">
        <p className="text-sm text-text-secondary text-center mb-6">
          欢迎加入 <span className="text-primary font-semibold">{config?.clientName || '用户端'}</span>
          ，请填写以下信息完成注册。
        </p>

        <div className="w-full max-w-md bg-white rounded-2xl p-6 shadow-md">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                placeholder="请输入昵称"
                value={name}
                onChange={(e) => setName(e.target.value)}
                leftIcon={<CircleUser size={18} />}
                clearable
                onClear={() => setName('')}
                required
              />
            </div>

            <div>
              <Input
                placeholder="请输入账号"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                leftIcon={<User size={18} />}
                clearable
                onClear={() => setUsername('')}
                required
              />
            </div>

            <div>
              <Input
                placeholder={inviteCodeLocked ? '邀请码（已自动填充）' : '请输入邀请码 (选填)'}
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                leftIcon={<Ticket size={18} />}
                clearable={false}
                disabled={inviteCodeLocked}
              />
              {inviteCodeLocked && <p className="text-xs text-primary mt-1">✓ 已使用推荐邀请码</p>}
            </div>

            <div>
              <Input
                placeholder="请输入密码"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                leftIcon={<Lock size={18} />}
                rightIcon={toggleIcon(showPassword, () => setShowPassword((prev) => !prev))}
                clearable
                onClear={() => setPassword('')}
                required
              />
            </div>

            <div>
              <Input
                placeholder="请确认密码"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                leftIcon={<Lock size={18} />}
                rightIcon={toggleIcon(showConfirmPassword, () => setShowConfirmPassword((prev) => !prev))}
                clearable
                onClear={() => setConfirmPassword('')}
                required
              />
            </div>

            <Button type="submit" variant="primary" block round disabled={loading}>
              {loading ? '注册中...' : '立即注册'}
            </Button>
          </form>
        </div>

        <div className="w-full max-w-md mt-6 space-y-3">
          {config?.appDownloadUrl && (
            <a href={config.appDownloadUrl} target="_blank" rel="noopener noreferrer">
              <Button
                variant="default"
                block
                round
                className="flex items-center justify-center gap-2 bg-white text-text-primary border border-border-color"
              >
                <span>⬇</span>
                下载 APP
              </Button>
            </a>
          )}
          <Button
            variant="default"
            block
            round
            onClick={() => navigate('/customer-service')}
            className="flex items-center justify-center gap-2 bg-white text-text-primary border border-border-color"
          >
            <span>💬</span>
            联系客服
          </Button>
        </div>

        <p className="text-sm text-text-secondary text-center mt-6">
          已有账号？
          <Button
            variant="default"
            size="mini"
            onClick={() => navigate('/login')}
            className="ml-2 border border-primary text-primary"
          >
            立即登录
          </Button>
        </p>
      </div>

      {/* 注册结果对话框 */}
      <AlertDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        type={dialogType}
        title={dialogTitle}
        description={dialogDescription}
        confirmText="前往登录"
        onConfirm={() => navigate('/login', { overwriteLastHistoryEntry: true })}
      />
    </div>
  );
}
